import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { message } from "antd";

const AuthContext = createContext();

const API_DOMAIN =
    process.env.REACT_APP_ADMIN_PORTAL_API_DOMAIN || "https://your-api-url";

export const AuthProvider = ({ children }) => {
    const { instance, accounts } = useMsal();
    const [accessToken, setAccessToken] = useState(null);
    const [userValidated, setUserValidated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [userInfo, setUserInfo] = useState(null);
    const [errorInfo, setErrorInfo] = useState("");

    // 🔹 FIXED: real multi-role support
    const [userRoles, setUserRoles] = useState([]);

    // 🔹 NEW: permission list (RBAC)
    const [userPermissions, setUserPermissions] = useState([]);

    // Shared loan number across pages
    const [globalLoanNumber, setGlobalLoanNumber] = useState("");

    const clearGlobalLoanNumber = useCallback(() => {
        setGlobalLoanNumber("");
    }, []);

    const login = () => {
        clearGlobalLoanNumber();
        instance.loginRedirect(loginRequest);
    };

    const logout = () => {
        setAccessToken(null);
        setUserInfo(null);
        setUserValidated(false);
        setUserRoles([]);
        setUserPermissions([]);
        clearGlobalLoanNumber();
        instance.clearCache();
        window.location.href = "/";
    };

    const getToken = useCallback(async () => {
        if (accounts.length === 0) return null;
        try {
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });
            setAccessToken(response.accessToken);
            return response.accessToken;
        } catch (error) {
            console.error("Token acquisition failed:", error);
            return null;
        }
    }, [accounts, instance]);

    const isAuthenticated = accounts.length > 0;
    const hasValidatedRef = useRef(false);
    useEffect(() => {
        const validateUser = async () => {
            if (!isAuthenticated || hasValidatedRef.current) {
                setLoading(false);
                return;
            }
            hasValidatedRef.current = true;

            try {
                // 🔹 REAL API FLOW (later)
                const token = await getToken();
                if (!token) {
                    setLoading(false);
                    return;
                }

                const res = await fetch(`${API_DOMAIN}/api/account/userinfo`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "x-api-key": process.env.REACT_APP_WEB_API_KEY,
                    },
                });

                if (res.ok) {
                    const user = await res.json();
                    setUserInfo(user);
                    setUserRoles(user.roles || []);
                    setUserPermissions(user.permissions || []);
                    setUserValidated(true);
                }
                else if (res.status === 401) {
                    message.error("Unauthorized: Token is invalid");
                }
                else if (res.status === 400) {
                    const errorData = await res.json();
                    setErrorInfo(errorData.error || "User does not have access.");
                }
                else {
                    message.error(
                        `Server responded with status: ${res.status}. Server is unavailable.`
                    );
                }
            } catch (err) {
                message.error("Failed to validate user, Server unavailable.");
            } finally {
                setLoading(false);
            }
        };

        validateUser();
    }, [isAuthenticated]);

    // Inside AuthProvider
const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
        const token = await getToken();
        if (!token) return;

        const res = await fetch(`${API_DOMAIN}/api/account/me/permissions`, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${token}`,
                "x-api-key": process.env.REACT_APP_WEB_API_KEY,
                "Content-Type": "application/json"
            }
        });

        if (res.ok) {
            const permissions = await res.json();
            setUserPermissions(permissions);
        } else {
            console.error("Failed to refresh permissions", res.status);
        }
    } catch (err) {
        console.error("Error refreshing permissions", err);
    }
}, [isAuthenticated, getToken]);


    return (
        <AuthContext.Provider
            value={{
                login,
                logout,
                isAuthenticated,
                account: accounts[0],
                getToken,
                accessToken,
                userValidated,
                loading,
                userRoles,
                userPermissions,
                userInfo,
                errorInfo,
                refreshPermissions,
                globalLoanNumber,
                setGlobalLoanNumber,
                clearGlobalLoanNumber,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;